import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ComponentRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewContainerRef, inject } from '@angular/core';
import { TranslateService } from "@ngx-translate/core";
import { IManagableObject } from "../../../admin-panel/IManagableObject";
import { AccessRights, ApplicationUserStatisticsView, UsersService } from "app/services/users.service";
import { ObjectGroupUserLink } from "app/models/ObjectGroupUserLink";
import { UserObjectGroupAcl } from "app/models/UserObjectGroupAcl";
import { OBJECT_GROUP_ACL_GROUPS, getObjectGroupAclPresets, getObjectGroupFullAccessMask } from "app/system/shared/model/object-group-acl";
import { ObjectsService } from "app/services/objects.service";
import { ObjectGroupViewModel } from "../../../admin-panel/ObjectGroupViewModel";
import { ObjectsFilterModel } from "../../pipes/FilterObjects.pipe";
import { CompaniesService, CompanyModel } from "app/services/companies.service";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { DialogService } from "app/services/dialog.service";
import { ObjectsManagementDialogComponent } from "../../../admin-panel/objects-management-dialog/objects-management-dialog.component";
import { ToastrService } from "ngx-toastr";
import { matchesAnySearchRule } from "../../pipes/filterByProperty";
import { Subject, takeUntil } from 'rxjs';

interface ObjectGroupUserItem {
  userId: number;
  name: string;
  email: string;
  acl: number;
  companyId: number;
}

interface ObjectGroupAccessUser extends IManagableObject {
  Email: string;
  CompanyId: number;
  Acl: number;
}

enum ObjectGroupDialogMode {
  Create = "create",
  Edit = "edit"
}

enum ObjectGroupAccessContext {
  Access = "access",
  Create = "create"
}

type GroupDialogTarget = IManagableObject | ObjectGroupViewModel | number | null | undefined;

@Component({
  selector: 'app-object-group-access',
  templateUrl: './object-group-access.component.html',
  styleUrls: ['./object-group-access.component.css'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ObjectGroupAccessComponent implements AfterViewInit, OnInit, OnDestroy {
  private translate = inject(TranslateService);
  private usersService = inject(UsersService);
  private objectsService = inject(ObjectsService);
  private companiesService = inject(CompaniesService);
  private formBuilder = inject(FormBuilder);
  private dialogService = inject(DialogService);
  private cdr = inject(ChangeDetectorRef);
  private toastr = inject(ToastrService);

  @Input()
  public isEditable: boolean = true;

  @Input()
  public objects: IManagableObject[] = [];

  @Input()
  public filter: ObjectsFilterModel | null = null;

  @Output()
  public groupsChanged: EventEmitter<void> = new EventEmitter<void>();

  public groupNameFilter: string = "";
  public dialogId: string = "object-group-access-dialog";

  public groups: ObjectGroupViewModel[] = [];

  public allGroupsItem: IManagableObject = new class implements IManagableObject {
    CompanyId: number;
    CanSelect?: boolean;
    Id: number;
    IsChecked: boolean;
    Name: string;
  };
  public selectedGroup: IManagableObject = this.allGroupsItem;

  public groupUsers: ObjectGroupUserItem[] = [];
  public availableUsers: ObjectGroupAccessUser[] = [];
  public selectedUserId: number = 0;
  public isLoadingUsers: boolean = false;
  public searchTerm: string = "";
  public isDialogVisible: boolean = false;
  public dialogMode: ObjectGroupDialogMode = ObjectGroupDialogMode.Create;
  public createGroupUsers: ObjectGroupUserItem[] = [];
  public createAvailableUsers: ObjectGroupAccessUser[] = [];
  public createSelectedUserId: number = 0;
  public createSelectedUser: ObjectGroupUserItem | null = null;
  public createSearchTerm: string = "";
  public hasPendingChanges: boolean = false;
  public activePresetMaskAccess: number | null = null;
  public activePresetMaskCreate: number | null = null;
  public accessObjectsFilter: string = "";
  public selectedGroupDetails: ObjectGroupViewModel | null = null;
  public accessSelectedObjects: IManagableObject[] = [];
  public createSelectedObjects: IManagableObject[] = [];
  public openedTabName: "objects" | "users" = "objects";
  private activeContext: ObjectGroupAccessContext = ObjectGroupAccessContext.Create;

  private users: ObjectGroupAccessUser[] = [];
  private usersById: Map<number, ObjectGroupAccessUser> = new Map();
  public selectedUser: ObjectGroupUserItem | null = null;
  private companies: CompanyModel[] = [];
  private hasLoadedGroups: boolean = false;
  private isLoadingGroups: boolean = false;
  private isLoadingGroupDetails: boolean = false;
  private loadingGroupId: number | null = null;
  private initialAccessObjectIds: number[] = [];
  public currentUserId: number = 0;
  private readonly userSort = (a: ObjectGroupUserItem, b: ObjectGroupUserItem) => {
    if (a.userId === this.currentUserId && b.userId !== this.currentUserId) return -1;
    if (b.userId === this.currentUserId && a.userId !== this.currentUserId) return 1;
    return (a.name || "").localeCompare(b.name || "");
  };

  public readonly aclOptions = [
    { value: UserObjectGroupAcl.EditObjects, label: "Edit objects" },
    { value: UserObjectGroupAcl.ChangeImei, label: "Edit IMEI" },
    { value: UserObjectGroupAcl.DeleteObjects, label: "Delete objects" },
    { value: UserObjectGroupAcl.SendCommands, label: "Send commands" },
    { value: UserObjectGroupAcl.EditGroup, label: "Edit group" },
    { value: UserObjectGroupAcl.ManageMembers, label: "Manage members" },
    { value: UserObjectGroupAcl.DeleteGroup, label: "Delete group" }
  ];

  public readonly aclGroups = OBJECT_GROUP_ACL_GROUPS;
  public readonly aclPresets = getObjectGroupAclPresets();
  public readonly UserObjectGroupAcl = UserObjectGroupAcl;
  public readonly DialogMode = ObjectGroupDialogMode;

  public draftGroup: ObjectGroupViewModel | null = null;
  public groupForm: FormGroup = this.buildGroupForm();
  public draftGroupName: string = "";

  @ViewChild("objectsListDialog", { read: ViewContainerRef, static: false }) objectsListContainer: ViewContainerRef;
  private objectsListDialog!: ComponentRef<ObjectsManagementDialogComponent>;
  @ViewChild("usersListDialog", { read: ViewContainerRef, static: false }) usersListContainer: ViewContainerRef;
  private usersListDialog!: ComponentRef<ObjectsManagementDialogComponent>;
  private readonly destroy$ = new Subject<void>();

  public onTermChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.groupNameFilter = input.value;
  }

  ngOnInit() {
    this.allGroupsItem.Name = this.translate.instant("All");
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() =>
      this.allGroupsItem.Name = this.translate.instant("All"));
    this.allGroupsItem.Id = -1;

    this.usersService.getUsersStatistics().subscribe(users => {
      this.users = users.map(user => this.toAccessUser(user));
      this.rebuildUserLookups();
      this.rehydrateLoadedUsers();
      this.refreshAvailableUsers();
      this.refreshCreateAvailableUsers();
      this.cdr.markForCheck();
    });
    this.currentUserId = this.usersService.getCurrentUser()?.Id ?? 0;

    this.companiesService.getCompanies().subscribe(companies => {
      this.companies = companies;
      this.cdr.markForCheck();
    });
  }

  ngAfterViewInit() {
    document.dispatchEvent(new CustomEvent("readyAgain"));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public openDialog(group?: GroupDialogTarget) {
    this.isDialogVisible = true;
    this.resetDialogState();
    this.loadGroups();
    this.openedTabName = "objects";
    const groupId = this.getGroupId(group);
    if (groupId) {
      this.activeContext = ObjectGroupAccessContext.Access;
      this.dialogMode = ObjectGroupDialogMode.Edit;
      this.selectedGroup = this.asManageableGroup(group) ?? this.allGroupsItem;
      this.selectedGroupDetails = this.asObjectGroupViewModel(group) ?? this.createGroupPlaceholder(groupId, group);
      this.loadGroupDetails(groupId);
    } else {
      this.activeContext = ObjectGroupAccessContext.Create;
      this.dialogMode = ObjectGroupDialogMode.Create;
      this.prepareCreateMode();
    }
    this.cdr.markForCheck();
  }

  public closeDialog() {
    this.isDialogVisible = false;
    this.resetDialogState();
    this.cdr.markForCheck();
  }

  public openTab(tab: "objects" | "users") {
    this.openedTabName = tab;
    this.cdr.markForCheck();
  }

  public isSelected(tab: "objects" | "users"): boolean {
    return this.openedTabName === tab;
  }

  public get activeGroupName(): string {
    return this.isEditMode() ? (this.selectedGroupDetails?.Name ?? "") : (this.draftGroupName ?? "");
  }

  public set activeGroupName(value: string) {
    if (this.isEditMode()) {
      if (this.selectedGroupDetails) this.selectedGroupDetails.Name = value;
      this.groupForm?.get("name")?.setValue(value, { emitEvent: false });
    } else {
      this.draftGroupName = value;
      this.groupForm?.get("name")?.setValue(value, { emitEvent: false });
    }
  }

  public getAclContext(): ObjectGroupAccessContext {
    return this.activeContext;
  }

  public get activeSelectedObjects(): IManagableObject[] {
    return this.isEditMode() ? this.accessSelectedObjects : this.createSelectedObjects;
  }

  public get isAclContextReady(): boolean {
    return this.canUseObjectAclContext();
  }

  public get activeSelectedUser(): ObjectGroupUserItem | null {
    return this.isEditMode() ? this.selectedUser : this.createSelectedUser;
  }

  public canEditUserAcl(item: ObjectGroupUserItem | null): boolean {
    return this.isEditable && !!item && item.userId !== this.currentUserId;
  }

  public canManageMembers(): boolean {
    if (!this.isEditable) return false;
    if (!this.isEditMode()) return true;
    if (this.isCurrentUserAdmin()) return true;
    const acl = this.getCurrentUserAclFromGroup(this.selectedGroupDetails);
    return (acl & UserObjectGroupAcl.ManageMembers) === UserObjectGroupAcl.ManageMembers;
  }

  public isManageMembersSelected(item: ObjectGroupUserItem | null): boolean {
    if (!item) return false;
    return (item.acl & UserObjectGroupAcl.ManageMembers) === UserObjectGroupAcl.ManageMembers;
  }

  // Some flags imply their parent permissions in the UI.
  private normalizeAcl(acl: number): number {
    if ((acl & UserObjectGroupAcl.ChangeImei) === UserObjectGroupAcl.ChangeImei)
      acl |= UserObjectGroupAcl.EditObjects;
    if ((acl & UserObjectGroupAcl.ManageMembers) === UserObjectGroupAcl.ManageMembers)
      return acl | UserObjectGroupAcl.EditGroup;
    return acl;
  }

  // Remove child permissions when the required parent flag is removed.
  private enforceAclDependencies(acl: number): number {
    let next = this.normalizeAcl(acl);
    if ((next & UserObjectGroupAcl.EditObjects) !== UserObjectGroupAcl.EditObjects) {
      next &= ~UserObjectGroupAcl.ChangeImei;
    }
    if ((next & UserObjectGroupAcl.EditGroup) !== UserObjectGroupAcl.EditGroup) {
      next &= ~UserObjectGroupAcl.ManageMembers;
    }
    return next;
  }

  public get activeAvailableUsers(): ObjectGroupAccessUser[] {
    return this.isEditMode() ? this.availableUsers : this.createAvailableUsers;
  }

  public get activeSelectedUserId(): number {
    return this.isEditMode() ? this.selectedUserId : this.createSelectedUserId;
  }

  public set activeSelectedUserId(val: number) {
    if (this.isEditMode()) {
      this.selectedUserId = val;
    } else {
      this.createSelectedUserId = val;
    }
  }

  public get activeSearchTerm(): string {
    return this.isEditMode() ? this.searchTerm : this.createSearchTerm;
  }

  public set activeSearchTerm(val: string) {
    if (this.isEditMode()) {
      this.searchTerm = val;
    } else {
      this.createSearchTerm = val;
    }
  }

  public get filteredActiveUsers(): ObjectGroupUserItem[] {
    return this.isEditMode() ? this.filteredGroupUsers : this.filteredCreateGroupUsers;
  }

  public get activePresetMask(): number | null {
    return this.isEditMode() ? this.activePresetMaskAccess : this.activePresetMaskCreate;
  }

  public addUser() {
    if (this.isEditMode()) {
      this.addUserToGroup();
    } else {
      this.addUserToCreateGroup();
    }
  }

  public removeUser(item: ObjectGroupUserItem) {
    if (this.isEditMode()) {
      this.removeUserFromGroup(item);
    } else {
      this.removeUserFromCreateGroup(item);
    }
  }

  public selectUserUnified(item: ObjectGroupUserItem) {
    if (this.isEditMode()) {
      this.selectUser(item);
    } else {
      this.selectCreateUser(item);
    }
  }

  public applyPresetUnified(mask: number) {
    if (!this.canEditUserAcl(this.activeSelectedUser)) return;
    if (this.isEditMode()) {
      this.applyPreset(mask);
    } else {
      this.applyCreatePreset(mask);
    }
  }

  public onAclChangeUnified(item: ObjectGroupUserItem, acl: number, event: Event) {
    if (!this.canEditUserAcl(item)) return;
    if (this.isEditMode()) {
      this.onAclChange(item, acl, event);
    } else {
      this.onCreateAclChange(item, acl, event);
    }
  }

  public openObjectsPickerForMode() {
    this.openObjectsPicker(this.getAclContext());
  }

  public openUsersPickerForMode() {
    this.openUsersPicker(this.getAclContext());
  }

  public removeSelectedObject(item: IManagableObject) {
    this.removeObjectFromContext(item);
  }

  public removeAllSelectedObjects() {
    if (!this.isEditable || this.activeSelectedObjects.length === 0) return;
    if (!this.ensureObjectAclContextReady()) return;
    if (!confirm(this.translate.instant("Clear") + "?")) return;
    this.activeContext = this.isEditMode()
      ? ObjectGroupAccessContext.Access
      : ObjectGroupAccessContext.Create;
    this.applySelectedObjectIdsToContext([]);
    this.cdr.markForCheck();
  }

  public openMgmtDropdown(group: ObjectGroupViewModel, event: MouseEvent) {
    this.groups.forEach(grp => {
      if (grp.Id == group.Id) {
        group.isMgmtOpen = !group.isMgmtOpen;
        group.mgmtyPos = event.clientY + 10;
        group.mgmtxPos = event.clientX + 10;
      } else {
        grp.isMgmtOpen = false;
      }
    });
  }

  public deleteGroup(group: IManagableObject) {
    let message = this.translate.instant("Delete");
    if (!confirm(message + " " + group.Name + "?")) return;
    this.objectsService.DeleteGroup(group).subscribe(() => {
      if (this.selectedGroup == group) {
        this.selectGroup(this.allGroupsItem);
      }
      this.loadGroups();
    });
  }

  public selectGroup(group: IManagableObject) {
    if (this.hasPendingChanges && !confirm(this.translate.instant("Discard changes?"))) {
      return;
    }
    this.selectedGroup = group;
    if (this.filter) {
      this.filter.group = this.toFilterGroup(group);
    }
    if (group?.Id > 0) {
      this.loadGroupDetails(group.Id);
    } else {
      this.groupUsers = [];
      this.selectedGroupDetails = null;
      this.selectedUser = null;
      this.accessSelectedObjects = [];
      this.refreshAvailableUsers();
    }
  }

  public addUserToGroup() {
    if (!this.canManageMembers() || !this.selectedGroup || this.selectedGroup.Id <= 0 || !this.selectedUserId) return;
    this.activeContext = ObjectGroupAccessContext.Access;
    this.addUsersToContext([this.selectedUserId]);
  }

  public removeUserFromGroup(item: ObjectGroupUserItem) {
    if (!this.canManageMembers() || !this.selectedGroup || this.selectedGroup.Id <= 0) return;
    let message = this.translate.instant("Delete");
    if (!confirm(message + " " + item.name + "?")) return;
    this.activeContext = ObjectGroupAccessContext.Access;
    this.removeUserFromContext(item);
  }

  public onAclChange(item: ObjectGroupUserItem, acl: number, event: Event) {
    if (!this.canManageMembers() || !this.selectedGroup || this.selectedGroup.Id <= 0) return;
    this.activeContext = ObjectGroupAccessContext.Access;
    this.updateUserAclForContext(item, acl, event);
  }

  public isAclSet(item: ObjectGroupUserItem, acl: number): boolean {
    const effectiveAcl = this.enforceAclDependencies(item.acl);
    return (effectiveAcl & acl) === acl;
  }

  public selectUser(item: ObjectGroupUserItem) {
    this.activeContext = ObjectGroupAccessContext.Access;
    this.setSelectedUserForContext(item);
  }

  public applyPreset(mask: number) {
    this.activeContext = ObjectGroupAccessContext.Access;
    this.applyPresetForContext(mask);
  }

  public addUserToCreateGroup() {
    if (!this.isEditable || !this.createSelectedUserId) return;
    this.activeContext = ObjectGroupAccessContext.Create;
    const userId = this.createSelectedUserId;
    this.addUsersToContext([userId]);
    this.createSelectedUser = this.createGroupUsers.find(user => user.userId === userId) ?? this.createSelectedUser;
  }

  public removeUserFromCreateGroup(item: ObjectGroupUserItem) {
    if (!this.isEditable) return;
    let message = this.translate.instant("Delete");
    if (!confirm(message + " " + item.name + "?")) return;
    this.activeContext = ObjectGroupAccessContext.Create;
    this.removeUserFromContext(item);
  }

  public selectCreateUser(item: ObjectGroupUserItem) {
    this.activeContext = ObjectGroupAccessContext.Create;
    this.setSelectedUserForContext(item);
  }

  public onCreateAclChange(item: ObjectGroupUserItem, acl: number, event: Event) {
    if (!this.isEditable) return;
    this.activeContext = ObjectGroupAccessContext.Create;
    this.updateUserAclForContext(item, acl, event);
  }

  public applyCreatePreset(mask: number) {
    this.activeContext = ObjectGroupAccessContext.Create;
    this.applyPresetForContext(mask);
  }

  public isAclAllowed(acl: number): boolean {
    const maxMask = this.getMaxAclMask();
    return (maxMask & acl) === acl;
  }

  public applyAccessChanges() {
    if (!this.ensureNameValid()) return;
    this.saveGroupDetails(() => {
      this.hasPendingChanges = false;
      this.closeDialog();
    });
  }

  public get filteredGroupUsers(): ObjectGroupUserItem[] {
    return this.filterUsersByTerm(this.groupUsers, this.searchTerm);
  }

  public get filteredCreateGroupUsers(): ObjectGroupUserItem[] {
    return this.filterUsersByTerm(this.createGroupUsers, this.createSearchTerm);
  }

  private loadGroupDetails(groupId: number) {
    if (this.isLoadingGroupDetails && this.loadingGroupId === groupId) return;
    this.isLoadingUsers = true;
    this.isLoadingGroupDetails = true;
    this.loadingGroupId = groupId;
    this.objectsService.GetObjectGroup(groupId)
      .subscribe((group: ObjectGroupViewModel) => {
        this.selectedGroupDetails = group;
        this.initialAccessObjectIds = [...(group?.objects ?? [])];
        const links = [...(group?.Users ?? [])];
        if (this.selectedGroupDetails) {
          this.selectedGroupDetails.Users = links;
        }
        const userMap = this.usersById;
        this.groupUsers = (links ?? []).map(link => this.toGroupUserItem(link, userMap));
        this.groupUsers.sort(this.userSort);
        this.selectedUser = this.groupUsers[0] ?? null;
        this.activePresetMaskAccess = null;
        this.refreshAvailableUsers();
        this.accessSelectedObjects = this.buildSelectedObjectsFromIds(group.objects ?? []);
        this.hasPendingChanges = false;
        this.isLoadingUsers = false;
        this.isLoadingGroupDetails = false;
        this.loadingGroupId = null;
        this.cdr.markForCheck();
      }, () => {
        this.isLoadingUsers = false;
        this.isLoadingGroupDetails = false;
        this.loadingGroupId = null;
        this.cdr.markForCheck();
      });
  }

  private toGroupUserItem(link: ObjectGroupUserLink, userMap: Map<number, ObjectGroupAccessUser>): ObjectGroupUserItem {
    const userId = link.userId;
    const acl = link.acl;
    const user = userMap.get(userId);
    return {
      userId,
      acl,
      name: user?.Name ?? "",
      email: user?.Email ?? "",
      companyId: user?.CompanyId ?? 0
    };
  }

  private refreshAvailableUsers() {
    const assignedIds = new Set(this.getContextUsers().map(user => user.userId));
    const availableUsers = this.users.filter(user => !assignedIds.has(user.Id));

    this.setAvailableUsersForContext(availableUsers);

    const selectedUserId = this.getSelectedUserIdForContext();
    const nextSelectedUserId = availableUsers.some(user => user.Id === selectedUserId)
      ? selectedUserId
      : (availableUsers[0]?.Id ?? 0);

    this.setSelectedUserIdForContext(nextSelectedUserId);
  }

  private refreshCreateAvailableUsers() {
    const previousContext = this.activeContext;
    this.activeContext = ObjectGroupAccessContext.Create;
    this.refreshAvailableUsers();
    this.activeContext = previousContext;
  }

  private saveGroupDetails(onSuccess?: () => void) {
    if (!this.selectedGroupDetails) return;
    this.activeContext = ObjectGroupAccessContext.Access;
    if (!this.validateTargetGroupAcl()) return;
    this.objectsService.CreateGroup(this.selectedGroupDetails)
      .subscribe(() => {
        if (onSuccess) onSuccess();
        this.groupsChanged.emit();
      });
  }

  private getContextUsers(): ObjectGroupUserItem[] {
    return this.activeContext === ObjectGroupAccessContext.Access ? this.groupUsers : this.createGroupUsers;
  }

  private setContextUsers(users: ObjectGroupUserItem[]) {
    if (this.activeContext === ObjectGroupAccessContext.Access) {
      this.groupUsers = users;
      return;
    }

    this.createGroupUsers = users;
  }

  private getSelectedUserForContext(): ObjectGroupUserItem | null {
    return this.activeContext === ObjectGroupAccessContext.Access ? this.selectedUser : this.createSelectedUser;
  }

  private setSelectedUserForContext(user: ObjectGroupUserItem | null) {
    if (this.activeContext === ObjectGroupAccessContext.Access) {
      this.selectedUser = user;
      this.setActivePresetMask(null);
      return;
    }

    this.createSelectedUser = user;
    this.setActivePresetMask(null);
  }

  private getSelectedUserIdForContext(): number {
    return this.activeContext === ObjectGroupAccessContext.Access ? this.selectedUserId : this.createSelectedUserId;
  }

  private setSelectedUserIdForContext(userId: number) {
    if (this.activeContext === ObjectGroupAccessContext.Access) {
      this.selectedUserId = userId;
      return;
    }

    this.createSelectedUserId = userId;
  }

  private setAvailableUsersForContext(users: ObjectGroupAccessUser[]) {
    if (this.activeContext === ObjectGroupAccessContext.Access) {
      this.availableUsers = users;
      return;
    }

    this.createAvailableUsers = users;
  }

  private setActivePresetMask(acl: number | null) {
    if (this.activeContext === ObjectGroupAccessContext.Access) {
      this.activePresetMaskAccess = acl;
      return;
    }

    this.activePresetMaskCreate = acl;
  }

  // Shared add flow for create/edit dialogs keeps local state and payload aligned.
  private addUsersToContext(userIds: number[]) {
    if (this.activeContext === ObjectGroupAccessContext.Access && !this.selectedGroupDetails) return;

    const defaultAcl = this.getDefaultAcl();
    const users = [...this.getContextUsers()];
    let changed = false;

    for (const userId of this.normalizeUserIds(userIds)) {
      if (users.some(user => user.userId === userId)) continue;
      users.push(this.createUserItem(userId, defaultAcl));
      changed = true;
    }

    if (!changed) return;

    const sortedUsers = this.sortUserItems(users);
    this.setContextUsers(sortedUsers);
    this.syncGroupLinksFromContextUsers();

    if (!this.getSelectedUserForContext()) {
      this.setSelectedUserForContext(sortedUsers[0] ?? null);
    }

    this.refreshAvailableUsers();
    if (this.activeContext === ObjectGroupAccessContext.Access) {
      this.hasPendingChanges = true;
    }
  }

  private removeUserFromContext(item: ObjectGroupUserItem) {
    if (item.userId === this.currentUserId) return;

    const nextUsers = this.getContextUsers().filter(user => user.userId !== item.userId);
    this.setContextUsers(nextUsers);
    this.syncGroupLinksFromContextUsers();

    if (this.getSelectedUserForContext()?.userId === item.userId) {
      this.setSelectedUserForContext(nextUsers[0] ?? null);
    }

    this.refreshAvailableUsers();
    if (this.activeContext === ObjectGroupAccessContext.Access) {
      this.hasPendingChanges = true;
    }
  }

  private updateUserAclForContext(item: ObjectGroupUserItem, acl: number, event: Event) {
    if (item.userId === this.currentUserId) return;

    const input = event.target as HTMLInputElement;
    let rawAcl = input.checked ? (item.acl | acl) : (item.acl & ~acl);

    if (!input.checked && acl === UserObjectGroupAcl.EditGroup) {
      rawAcl &= ~UserObjectGroupAcl.ManageMembers;
    }

    if (!input.checked && acl === UserObjectGroupAcl.EditObjects) {
      rawAcl &= ~UserObjectGroupAcl.ChangeImei;
    }

    const nextAcl = this.enforceAclDependencies(rawAcl);
    if (nextAcl === item.acl) return;

    const maxMask = this.getMaxAclMask();
    item.acl = this.enforceAclDependencies(nextAcl & maxMask);
    this.syncGroupLinksFromContextUsers();

    if (this.activeContext === ObjectGroupAccessContext.Access) {
      this.hasPendingChanges = true;
    }

    this.cdr.markForCheck();
  }

  private applyPresetForContext(mask: number) {
    const selectedUser = this.getSelectedUserForContext();
    if (!selectedUser || selectedUser.userId === this.currentUserId) return;

    const maxMask = this.getMaxAclMask();
    selectedUser.acl = this.enforceAclDependencies(mask) & maxMask;
    selectedUser.acl = this.enforceAclDependencies(selectedUser.acl);
    this.setActivePresetMask(selectedUser.acl);
    this.syncGroupLinksFromContextUsers();

    if (this.activeContext === ObjectGroupAccessContext.Access) {
      this.hasPendingChanges = true;
    }

    this.cdr.markForCheck();
  }

  // Only edit mode writes directly into the loaded group; create mode serializes on save.
  private syncGroupLinksFromContextUsers() {
    if (this.activeContext !== ObjectGroupAccessContext.Access || !this.selectedGroupDetails) return;

    this.selectedGroupDetails.Users = this.getContextUsers()
      .map(user => ({ userId: user.userId, acl: user.acl }));
  }

  private sortUserItems(users: ObjectGroupUserItem[]): ObjectGroupUserItem[] {
    return [...users].sort(this.userSort);
  }

  private normalizeUserIds(userIds: number[]): number[] {
    return [...new Set(userIds.filter(userId => userId > 0))];
  }

  private filterUsersByTerm(users: ObjectGroupUserItem[], term: string): ObjectGroupUserItem[] {
    const normalizedTerm = term.trim();
    if (!normalizedTerm) return users;

    return users.filter(user =>
      matchesAnySearchRule(normalizedTerm, user.name, user.email)
    );
  }

  private getSelectedObjectIds(): number[] {
    return this.activeContext === ObjectGroupAccessContext.Access
      ? [...(this.selectedGroupDetails?.objects ?? [])]
      : this.createSelectedObjects.map(object => object.Id);
  }

  // Keep selected chips and DTO payload in sync for the active dialog mode.
  private applySelectedObjectIdsToContext(objectIds: number[]) {
    const normalizedIds = [...new Set(objectIds)];
    const selectedObjects = this.buildSelectedObjectsFromIds(normalizedIds);

    if (this.activeContext === ObjectGroupAccessContext.Access) {
      if (!this.selectedGroupDetails) return;
      this.selectedGroupDetails.objects = normalizedIds;
      this.accessSelectedObjects = selectedObjects;
      this.hasPendingChanges = true;
      return;
    }

    this.createSelectedObjects = selectedObjects;
    if (this.draftGroup) {
      this.draftGroup.objects = normalizedIds;
    }
  }

  private removeObjectFromContext(item: IManagableObject) {
    const nextIds = this.getSelectedObjectIds().filter(id => id !== item.Id);
    this.applySelectedObjectIdsToContext(nextIds);
  }

  private buildSelectableUsers(): IManagableObject[] {
    const assignedIds = new Set(this.getContextUsers().map(user => user.userId));

    return this.users.map(user => ({
      Id: user.Id,
      Name: user.Name,
      IsChecked: false,
      CompanyId: user.CompanyId,
      CanSelect: user.CanSelect !== false && !assignedIds.has(user.Id)
    }));
  }

  private loadGroups() {
    if (this.isLoadingGroups) return;
    this.hasLoadedGroups = false;
    this.isLoadingGroups = true;
    this.objectsService.GetObjectGroups().subscribe(groups => {
      this.groups = groups;
      this.hasLoadedGroups = true;
      this.isLoadingGroups = false;
      this.cdr.markForCheck();
    }, () => {
      this.hasLoadedGroups = false;
      this.isLoadingGroups = false;
      this.cdr.markForCheck();
    });
  }

  public getCompanyName(companyId: number): string {
    return this.companies.find(x => x.Id === companyId)?.Name ?? "";
  }

  private getMaxAclMask(): number {
    const currentUser = this.usersService.getCurrentUser();
    if (currentUser?.CanDo(AccessRights.EDITUSERS) || currentUser?.CanDo(AccessRights.EDIT_ALL_USERS)) {
      return getObjectGroupFullAccessMask();
    }
    if (this.activeContext === ObjectGroupAccessContext.Create) {
      const current = this.createGroupUsers.find(x => x.userId === this.currentUserId);
      return current?.acl ?? 0;
    }
    return this.getCurrentUserAclFromGroup(this.selectedGroupDetails);
  }

  private getDefaultAcl(_context: ObjectGroupAccessContext = this.activeContext): number {
    return 0;
  }

  private createUserItem(userId: number, acl: number): ObjectGroupUserItem {
    const user = this.usersById.get(userId);
    return {
      userId,
      name: user?.Name ?? "",
      email: user?.Email ?? "",
      acl,
      companyId: user?.CompanyId ?? 0
    };
  }

  public saveGroupEdit() {
    if (!this.draftGroup || !this.groupForm) return;
    if (!this.ensureNameValid()) return;
    const name = (this.isEditMode()
      ? this.selectedGroupDetails?.Name
      : this.draftGroupName)?.trim();
    this.draftGroup.Name = name ?? "";
    this.draftGroup.objects = this.createSelectedObjects.map(x => x.Id);
    if (this.isCreateMode()) {
      this.draftGroup.Users = this.createGroupUsers.map(x => ({ userId: x.userId, acl: x.acl }));
    }
    this.activeContext = ObjectGroupAccessContext.Create;
    if (!this.validateTargetGroupAcl()) return;
    this.objectsService.CreateGroup(this.draftGroup).subscribe(() => {
      this.groupsChanged.emit();
      this.closeDialog();
    });
  }

  private resetDialogState() {
    this.groupNameFilter = "";
    this.searchTerm = "";
    this.createSearchTerm = "";
    this.accessObjectsFilter = "";
    this.selectedUserId = 0;
    this.selectedUser = null;
    this.selectedGroup = this.allGroupsItem;
    this.selectedGroupDetails = null;
    this.groupUsers = [];
    this.createGroupUsers = [];
    this.createSelectedUser = null;
    this.createSelectedUserId = 0;
    this.initialAccessObjectIds = [];
    this.accessSelectedObjects = [];
    this.createSelectedObjects = [];
    this.hasPendingChanges = false;
    this.draftGroupName = "";
    this.draftGroup = null;
    this.activePresetMaskAccess = null;
    this.activePresetMaskCreate = null;
    this.groupForm = this.buildGroupForm();
  }

  private prepareCreateMode() {
    this.draftGroup = new ObjectGroupViewModel();
    this.draftGroup.CompanyId = this.usersService.getCurrentUser()?.CompanyId ?? 0;
    this.draftGroupName = this.draftGroup.Name ?? "";
    this.groupForm = this.buildGroupForm(this.draftGroupName);
    const currentUser = this.usersService.getCurrentUser();
    const existing = this.createGroupUsers.find(x => x.userId === currentUser?.Id);
    if (!this.isCurrentUserAdmin() && !existing && currentUser) {
      this.createGroupUsers = [this.createUserItem(currentUser.Id, getObjectGroupFullAccessMask())];
    }
    this.createGroupUsers.sort(this.userSort);
    this.createSelectedUser = this.createGroupUsers[0] ?? null;
    this.activePresetMaskCreate = null;
    this.refreshCreateAvailableUsers();
  }

  public openObjectsPicker(context: ObjectGroupAccessContext) {
    if (!this.isEditable) return;
    this.activeContext = context;
    if (!this.ensureObjectAclContextReady()) return;
    const selectedIds = new Set<number>(this.getSelectedObjectIds());
    const allObjects = this.objects.map(x => Object.assign({}, x));
    this.objectsListDialog = this.dialogService.open(this.objectsListContainer, ObjectsManagementDialogComponent);
    this.objectsListDialog.instance.onOpen.subscribe(() => {
      this.objectsListDialog.instance.filter.companyIds = [...(this.filter?.companyIds ?? [])];
      this.objectsListDialog.instance.showCompanyFilterAndColumn = true;
      this.objectsListDialog.instance.groups = this.groups;
      this.objectsListDialog.instance.objects = allObjects
        .filter(d => !selectedIds.has(d.Id))
        .filter(d => this.canAssignObjectToTargetGroup(d.Id))
        .map(obj => {
          obj.IsChecked = false;
          return obj;
        });
    });
    this.objectsListDialog.instance.onSubmit.subscribe(() => {
      this.objectsListDialog.instance.objects.forEach(obj => {
        if (obj && obj.IsChecked && !selectedIds.has(obj.Id)) {
          selectedIds.add(obj.Id);
        }
      });
      this.applySelectedObjectIdsToContext(Array.from(selectedIds));
      this.objectsListDialog.instance.close();
      this.cdr.markForCheck();
    });
    this.objectsListDialog.instance.open(null);
  }

  private openUsersPicker(context: ObjectGroupAccessContext) {
    if (!this.isEditable) return;
    this.activeContext = context;
    const companyLookup = this.companies.reduce((lookup, company) => {
      lookup[company.Id] = company.Name;
      return lookup;
    }, {} as { [key: number]: string });

    this.usersListDialog = this.dialogService.open(this.usersListContainer, ObjectsManagementDialogComponent);
    this.usersListDialog.instance.onOpen.subscribe(() => {
      this.usersListDialog.instance.groups = [];
      this.usersListDialog.instance.title = this.translate.instant("Users list");
      this.usersListDialog.instance.companyLookup = companyLookup;
      this.usersListDialog.instance.showCompanyFilterAndColumn = true;
      this.usersListDialog.instance.objects = [];
      this.usersListDialog.instance.isLoading = true;
      const assignedIds = new Set(this.getContextUsers().map(user => user.userId));
      const usersRequest = context === ObjectGroupAccessContext.Access && this.selectedGroupDetails?.Id
        ? this.usersService.getUsersForGroup(this.selectedGroupDetails.Id)
        : this.usersService.getUsersForNewGroup(this.draftGroup?.CompanyId || this.usersService.getCurrentUser()?.CompanyId || 0);

      usersRequest.subscribe(users => {
        this.usersListDialog.instance.objects = (users ?? [])
          .map(user => {
            const candidate = this.toAccessUser(user);
            candidate.CanSelect = candidate.CanSelect !== false && !assignedIds.has(candidate.Id);
            return candidate;
          })
          .sort((a, b) => Number(b.CanSelect) - Number(a.CanSelect));

        this.usersListDialog.instance.isLoading = false;
        this.cdr.markForCheck();
      }, () => {
        this.usersListDialog.instance.objects = [];
        this.usersListDialog.instance.isLoading = false;
        this.cdr.markForCheck();
      });
    });
    this.usersListDialog.instance.onSubmit.subscribe(() => {
      const selectedIds = this.usersListDialog.instance.objects
        .filter(obj => obj && obj.IsChecked)
        .map(obj => obj.Id);
      this.addUsersToContext(selectedIds);
      this.usersListDialog.instance.close();
      this.cdr.markForCheck();
    });
    this.usersListDialog.instance.open(null);
  }

  private buildSelectedObjectsFromIds(ids: number[]): IManagableObject[] {
    const idSet = new Set<number>(ids ?? []);
    return this.objects
      .filter(o => idSet.has(o.Id))
      .map(o => Object.assign({}, o));
  }

  private validateTargetGroupAcl(): boolean {
    if (!this.ensureObjectAclContextReady()) return false;

    const violation = this.findTargetGroupAclViolation();
    if (!violation) return true;

    this.toastr.error(
      `Cannot add "${violation.objectName}" to "${violation.targetGroupName}" because the target group gives you more permissions than you currently have for this object.`
    );

    return false;
  }

  // Target group must not expand current permissions for the object.
  private findTargetGroupAclViolation(): { objectName: string; sourceGroupName: string; targetGroupName: string } | null {
    const objectIds = this.getAddedObjectIds();
    if (objectIds.length === 0) return null;

    const targetAcl = this.getCurrentUserAclForContext();
    const targetGroupId = this.activeContext === ObjectGroupAccessContext.Access ? (this.selectedGroupDetails?.Id ?? null) : null;
    const targetGroupName = this.getTargetGroupName();

    for (const objectId of objectIds) {
      const sourceGroups = this.getSourceGroupsForObject(objectId, targetGroupId);
      const objectAcl = this.getCurrentUserObjectAcl(sourceGroups);
      if ((objectAcl & targetAcl) === targetAcl) {
        continue;
      }

      const sourceGroup = sourceGroups.find(group => this.getCurrentUserAclFromGroup(group) !== 0) ?? sourceGroups[0];
      if (!sourceGroup) continue;

      return {
        objectName: this.objects.find(x => x.Id === objectId)?.Name ?? `${objectId}`,
        sourceGroupName: sourceGroup.Name ?? `${sourceGroup.Id}`,
        targetGroupName
      };
    }

    return null;
  }

  private canAssignObjectToTargetGroup(objectId: number): boolean {
    if (this.isCurrentUserAdmin()) {
      return true;
    }

    const targetAcl = this.getCurrentUserAclForContext();
    const targetGroupId = this.activeContext === ObjectGroupAccessContext.Access ? (this.selectedGroupDetails?.Id ?? null) : null;
    const sourceGroups = this.getSourceGroupsForObject(objectId, targetGroupId);

    const objectAcl = this.getCurrentUserObjectAcl(sourceGroups);
    return (objectAcl & targetAcl) === targetAcl;
  }

  private getAddedObjectIds(): number[] {
    if (this.activeContext === ObjectGroupAccessContext.Create) {
      return [...(this.draftGroup?.objects ?? [])];
    }

    const currentIds = this.selectedGroupDetails?.objects ?? [];
    return currentIds.filter(id => !this.initialAccessObjectIds.includes(id));
  }

  private getSourceGroupsForObject(objectId: number, targetGroupId: number | null): ObjectGroupViewModel[] {
    return (this.groups ?? [])
      .filter(group => group?.Id !== targetGroupId && (group?.objects ?? []).includes(objectId));
  }

  private getCurrentUserObjectAcl(groups: ObjectGroupViewModel[]): number {
    return groups.reduce((acl, group) => acl | this.getCurrentUserAclFromGroup(group), 0);
  }

  private getCurrentUserAclForContext(): number {
    if (this.isCurrentUserAdmin()) {
      return getObjectGroupFullAccessMask();
    }

    if (this.activeContext === ObjectGroupAccessContext.Create) {
      return this.createGroupUsers.find(x => x.userId === this.currentUserId)?.acl ?? 0;
    }

    return this.getCurrentUserAclFromGroup(this.selectedGroupDetails);
  }

  private getCurrentUserAclFromGroup(group?: ObjectGroupViewModel | null): number {
    if (!group) return 0;
    if (this.isCurrentUserAdmin()) {
      return getObjectGroupFullAccessMask();
    }

    // Group payload is fresher than the cached profile links.
    const current = group.Users?.find(link => link.userId === this.currentUserId);
    const currentAcl = this.toOptionalNumber(current?.acl);
    if (currentAcl !== null) return currentAcl;

    const currentUser = this.usersService.getCurrentUser();
    const currentUserLink = currentUser?.ObjectGroupLinks?.find(link => link.ObjectGroupId === group.Id);
    const currentUserLinkAcl = this.toOptionalNumber(currentUserLink?.ACL);
    if (currentUserLinkAcl !== null) return currentUserLinkAcl;
    return 0;
  }

  private getTargetGroupName(): string {
    if (this.activeContext === ObjectGroupAccessContext.Create) {
      return (this.draftGroup?.Name || this.draftGroupName || this.translate.instant("New group")).trim();
    }

    return (this.selectedGroupDetails?.Name || this.translate.instant("Group")).trim();
  }

  private isCurrentUserAdmin(): boolean {
    const currentUser = this.usersService.getCurrentUser();
    return !!currentUser && (
      this.hasAdminAcl(currentUser.Acl)
    );
  }

  private hasAdminAcl(acl: number | null | undefined): boolean {
    const numericAcl = this.toNumberOrZero(acl);
    return (numericAcl & (1 << AccessRights.EDITUSERS)) !== 0
      || (numericAcl & (1 << AccessRights.EDIT_ALL_USERS)) !== 0;
  }

  private getGroupId(group: GroupDialogTarget): number | null {
    if (this.isGroupId(group)) return group;
    return group && group.Id > 0 ? group.Id : null;
  }

  private asObjectGroupViewModel(group: GroupDialogTarget): ObjectGroupViewModel | null {
    return group && !this.isGroupId(group) && "objects" in group ? group : null;
  }

  // Allows opening the dialog from a lightweight group item before full details are loaded.
  private createGroupPlaceholder(groupId: number, group: GroupDialogTarget): ObjectGroupViewModel {
    const placeholder = new ObjectGroupViewModel();
    placeholder.Id = groupId;
    const manageableGroup = this.asManageableGroup(group);
    if (manageableGroup) placeholder.Name = manageableGroup.Name;
    return placeholder;
  }

  private toAccessUser(user: ApplicationUserStatisticsView): ObjectGroupAccessUser {
    return {
      Id: user.Id,
      Name: user.Name ?? "",
      Email: user.Email ?? "",
      CompanyId: user.CompanyId ?? 0,
      IsChecked: false,
      Acl: user.Acl ?? 0,
      CanSelect: user.CanSelect
    };
  }

  private rebuildUserLookups() {
    this.usersById = new Map(this.users.map(user => [user.Id, user]));
  }

  // Backfill names/emails for already loaded group members when the users directory arrives later.
  private rehydrateLoadedUsers() {
    this.groupUsers = this.sortUserItems(this.groupUsers.map(user => this.createUserItem(user.userId, user.acl)));
    this.createGroupUsers = this.sortUserItems(this.createGroupUsers.map(user => this.createUserItem(user.userId, user.acl)));

    if (this.selectedUser) {
      this.selectedUser = this.groupUsers.find(user => user.userId === this.selectedUser?.userId) ?? this.groupUsers[0] ?? null;
    }

    if (this.createSelectedUser) {
      this.createSelectedUser = this.createGroupUsers.find(user => user.userId === this.createSelectedUser?.userId) ?? this.createGroupUsers[0] ?? null;
    }
  }

  private ensureNameValid(): boolean {
    const name = (this.isEditMode()
      ? this.selectedGroupDetails?.Name
      : this.draftGroupName)?.trim();
    const control = this.groupForm?.get("name");
    if (!name) {
      control?.setErrors({ required: true });
      control?.markAsTouched();
      this.cdr.markForCheck();
      return false;
    }
    if (control?.hasError("required")) {
      control.setErrors(null);
    }
    return true;
  }

  private buildGroupForm(name: string = ""): FormGroup {
    return this.formBuilder.group({
      name: new FormControl(name, Validators.required)
    });
  }

  private toFilterGroup(group: IManagableObject): ObjectGroupViewModel {
    const objectGroup = this.asObjectGroupViewModel(group);
    return objectGroup ?? this.createGroupPlaceholder(group.Id, group);
  }

  private canUseObjectAclContext(): boolean {
    if (!this.hasLoadedGroups || this.isLoadingGroups) return false;
    if (this.activeContext === ObjectGroupAccessContext.Access) {
      return !this.isLoadingGroupDetails && !!this.selectedGroupDetails?.Id;
    }

    return true;
  }

  private ensureObjectAclContextReady(): boolean {
    if (this.canUseObjectAclContext()) {
      return true;
    }

    if (!this.hasLoadedGroups || this.isLoadingGroups) {
      this.toastr.warning("Please wait until group permissions are loaded.");
      return false;
    }

    this.toastr.warning("Please wait until the target group is loaded.");
    return false;
  }

  private isEditMode(): boolean {
    return this.dialogMode === ObjectGroupDialogMode.Edit;
  }

  private isCreateMode(): boolean {
    return this.dialogMode === ObjectGroupDialogMode.Create;
  }

  private isGroupId(group: GroupDialogTarget): group is number {
    return Number.isInteger(group) && Number(group) > 0;
  }

  private asManageableGroup(group: GroupDialogTarget): IManagableObject | null {
    return group && !this.isGroupId(group) ? group : null;
  }

  private toOptionalNumber(value: number | null | undefined): number | null {
    const numberValue = Number(value);
    return Number.isNaN(numberValue) ? null : numberValue;
  }

  private toNumberOrZero(value: number | null | undefined): number {
    return this.toOptionalNumber(value) ?? 0;
  }
}
